import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { EnvelopeSimple, ChatText, CheckCircle } from "@phosphor-icons/react";
import { Input } from "./ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const receiptText = (fare) => {
  const lines = ["DS CABS receipt", `Fare $${Number(fare.price).toFixed(2)}`];
  if (fare.night_surcharge > 0) lines.push(`Night surcharge $${Number(fare.night_surcharge).toFixed(2)}`);
  lines.push(`Tax $${Number(fare.tax).toFixed(2)}`, `Total $${Number(fare.total).toFixed(2)} CAD`, "Thank you!");
  return lines.join("\n");
};

// Post-payment: let the customer send themselves a receipt by email or text.
export const ReceiptOptions = ({ fare, defaultEmail = "", defaultPhone = "" }) => {
  const [mode, setMode] = useState(null); // "email" | "sms"
  const [val, setVal] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState("");

  const open = (m) => { setMode(m); setVal(m === "email" ? defaultEmail : defaultPhone); };

  const send = async () => {
    if (!val.trim()) { toast.error(`Enter your ${mode === "email" ? "email" : "phone number"}.`); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/receipt`, { fare_id: fare.fare_id, channel: mode, to: val.trim() });
      setSent(mode);
      setMode(null);
      toast.success(`Receipt sent by ${mode === "email" ? "email" : "text"}!`);
    } catch (err) {
      if (mode === "sms") {
        // Text receipts aren't auto-sent yet — open the phone's messages app pre-filled.
        window.location.href = `sms:${val.trim()}?&body=${encodeURIComponent(receiptText(fare))}`;
        setSent("sms");
        setMode(null);
      } else {
        toast.error(err.response?.data?.detail || "Couldn't send — check the address and try again.");
      }
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="receipt-options" className="mt-6 w-full border-2 border-ink/10 bg-paper p-4">
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-ink/60 mb-3">Want a receipt?</p>

      {sent && !mode && (
        <p data-testid="receipt-sent" className="flex items-center gap-2 text-brandblue font-semibold text-sm">
          <CheckCircle weight="fill" size={18} /> Receipt sent by {sent === "email" ? "email" : "text"}.
        </p>
      )}

      {!mode && (
        <div className="flex flex-wrap gap-2.5">
          <button data-testid="receipt-email-btn" onClick={() => open("email")}
            className="flex items-center gap-2 bg-white text-ink font-bold text-sm px-4 py-2.5 border-2 border-ink hover:bg-sun transition-colors">
            <EnvelopeSimple weight="fill" size={16} /> Email receipt
          </button>
          <button data-testid="receipt-sms-btn" onClick={() => open("sms")}
            className="flex items-center gap-2 bg-white text-ink font-bold text-sm px-4 py-2.5 border-2 border-ink hover:bg-sun transition-colors">
            <ChatText weight="fill" size={16} /> Text receipt
          </button>
        </div>
      )}

      {mode && (
        <div className="flex flex-col sm:flex-row gap-2">
          <Input data-testid="receipt-input" value={val} onChange={(e) => setVal(e.target.value)}
            placeholder={mode === "email" ? "you@email.com" : "Your mobile number"}
            className="border-2 border-ink rounded-none h-11 bg-white flex-1" />
          <button data-testid="receipt-send-btn" onClick={send} disabled={loading}
            className="bg-ink text-white font-bold px-5 py-2.5 border-2 border-ink hover:bg-brandblue transition-colors disabled:opacity-60">
            {loading ? "Sending…" : "Send"}
          </button>
        </div>
      )}
    </div>
  );
};
